import type { ExtractResource, ResourceType } from '@medplum/fhirtypes'
import { getResourceDate } from '@seen/core/display'
import { getReferenceString } from '@seen/core/references'
import { type AnyFunction, recordEntries } from '@seen/core/utils'
import Fuse, {
  type Expression,
  type FuseOptionKey,
  type IFuseOptions,
} from 'fuse.js'
import { get, isEmpty, orderBy } from 'lodash-es'
import { SEARCH_WEIGHT } from '#src/search/weight.ts'

export interface Indexer<T extends ResourceType> {
  getKey: (resource: ExtractResource<T>) => string
  weight?: number
}

export type IndexerRecord<T extends ResourceType> = { [K in T]: Indexer<K>[] }

type Result<T extends ResourceType> = {
  resource: ExtractResource<T>
  score: number // Number between 0 and 1. 0 indicates a perfect match, while 1 indicates a complete mismatch.
}

/**
 * FuzzySearcher provides fuzzy search over FHIR resources.
 * It uses Fuse.js for efficient and flexible searching.
 */
export class FuzzySearcher<T extends ResourceType> {
  private _fuse: Fuse<ExtractResource<T>>
  private _onUpdate: AnyFunction | null

  constructor({ indexerRecord }: { indexerRecord: IndexerRecord<T> }) {
    this._fuse = this.initializeFuse([], indexerRecord)
    this._onUpdate = null
  }

  search(pattern: string | Expression): Result<T>[] {
    if (isEmpty(pattern)) {
      return this.getItemsSortedByDate().map((resource) => ({
        resource,
        score: 1,
      }))
    }
    return this.searchItemsSortedByScoreThenDate(pattern)
  }

  addResources(resources: ExtractResource<T>[]): void {
    if (isEmpty(resources)) {
      return
    }
    const resourceIds = resources.map(getReferenceString)
    this._fuse.remove((resource) =>
      resourceIds.includes(getReferenceString(resource))
    )
    resources.forEach((resource) => {
      this._fuse.add(resource)
    })
    this._onUpdate?.()
  }

  setResources(resources: ExtractResource<T>[]): void {
    this._fuse.setCollection(resources)
    this._onUpdate?.()
  }

  updateIndexerRecord(indexerRecord: IndexerRecord<T>): void {
    this._fuse = this.initializeFuse(this.getItems(), indexerRecord)
    this._onUpdate?.()
  }

  // TODO: Use an event target instead: https://github.com/medplum/medplum/blob/120369e51872f6ece35e203ca8b70cb931a12095/packages/core/src/eventtarget.ts#L41
  subscribe(onUpdate: AnyFunction): void {
    this._onUpdate = onUpdate
  }

  unsubscribe(): void {
    this._onUpdate = null
  }

  private initializeFuse(
    initialItems: ExtractResource<T>[],
    indexerRecord: IndexerRecord<T>
  ): Fuse<ExtractResource<T>> {
    return new Fuse<ExtractResource<T>>(
      initialItems,
      getFuseOptions(indexerRecord)
    )
  }

  private getItems(): ExtractResource<T>[] {
    return get(this._fuse.getIndex(), 'docs', [])
  }

  private getItemsSortedByDate(): ExtractResource<T>[] {
    return orderBy(this.getItems(), (r) => getResourceDate(r), 'desc')
  }

  private searchItemsSortedByScoreThenDate(
    pattern: string | Expression
  ): Result<T>[] {
    return orderBy(
      this._fuse.search(pattern),
      [(r) => r.score, (r) => getResourceDate(r.item)],
      ['asc', 'desc']
    ).map((r) => ({
      resource: r.item,
      score: r.score ?? 1,
    }))
  }
}

function getFuseOptions<T extends ResourceType>(
  indexerRecord: IndexerRecord<T>
): IFuseOptions<ExtractResource<T>> {
  return {
    keys: recordEntries(indexerRecord).flatMap(([resourceType, indexers]) =>
      indexers.map((indexer, idx) => getFuseKey(indexer, { resourceType, idx }))
    ),
    includeScore: true,
  }
}

function getFuseKey<T extends ResourceType>(
  indexer: Indexer<T>,
  {
    resourceType,
    idx,
  }: {
    resourceType: T
    idx: number
  }
): FuseOptionKey<ExtractResource<T>> {
  return {
    name: `${resourceType}-${idx}`,
    getFn: (resource: ExtractResource<T>) => {
      // getFn gets called for all results so we need the resource check here
      if (resource.resourceType !== resourceType) {
        return ''
      }
      return indexer.getKey(resource)
    },
    weight: indexer.weight ?? SEARCH_WEIGHT.DEFAULT,
  }
}
